// constitution.js — TCM Body Constitution self-assessment (34 items)
//
// Spec: constitution/docs/gloria_constitution_quiz_tech_spec_v2_patch.md (v3)
//       The nine constitution copy blocks come from v2 section 4, which v3 leaves unchanged.
//
// All constitution-related wording in the data tables below is finalized copy and must not be
// reworded here. UI microcopy (buttons, screen transitions, section titles) was written during
// implementation and is safe to edit.

// ─── Questions (34 unique items) ──────────────────────────────
// px02/px03 from v2 are deliberately absent: the official questionnaire reuses the Qi Deficiency
// items qx01/qx04 for the Balanced scale with reverse scoring instead of asking them twice.
// See TYPE_SCORING_MAP below. Do not re-add them.
const QUESTIONS = [
  { id: 'px01', text: 'I have plenty of energy.' },
  { id: 'px04', text: 'I adapt well to changes in my environment and daily life.' },

  { id: 'qx01', text: 'I get tired easily.' },
  { id: 'qx02', text: 'I get short of breath easily, even with light activity.' },
  { id: 'qx03', text: 'I break into a sweat with just a bit of activity.' },
  { id: 'qx04', text: 'My voice sounds weak or low when I talk.' },

  { id: 'yx01', text: 'My hands and feet are often cold.' },
  { id: 'yx02', text: 'I feel the cold more than other people and tend to bundle up.' },
  { id: 'yx03', text: "Cold or iced foods and drinks don't agree with me." },
  { id: 'yx04', text: 'Getting chilled or eating cold food tends to give me diarrhea.' },

  { id: 'yinx01', text: 'The palms of my hands and soles of my feet often feel warm.' },
  { id: 'yinx02', text: 'My mouth and throat often feel dry, and I crave water.' },
  { id: 'yinx03', text: 'I tend toward constipation or dry stools.' },
  { id: 'yinx04', text: 'My eyes often feel dry.' },

  { id: 'ts01', text: 'My belly feels soft and puffy, and I gain weight easily.' },
  { id: 'ts02', text: 'I often feel heavy or sluggish in my body.' },
  { id: 'ts03', text: 'My mouth often feels sticky or coated.' },
  { id: 'ts04', text: 'I have a lot of phlegm, or a constant feeling of phlegm stuck in my throat.' },

  { id: 'sr01', text: 'My face or scalp gets oily easily.' },
  { id: 'sr02', text: 'I break out in acne or skin eruptions easily.' },
  { id: 'sr03', text: 'I have a bitter taste in my mouth, or bad breath.' },
  { id: 'sr04', text: "My stools feel sticky and incomplete, like I can't fully empty my bowels." },

  { id: 'xy01', text: 'I bruise easily, without remembering how it happened.' },
  { id: 'xy02', text: 'My complexion looks dull, or I get dark spots easily.' },
  { id: 'xy03', text: "I'm fairly forgetful." },
  { id: 'xy04', text: 'My lips tend to look darker than usual.' },

  { id: 'qy01', text: 'I often feel down or low in mood.' },
  { id: 'qy02', text: 'I sigh a lot, for no particular reason.' },
  { id: 'qy03', text: 'I feel tightness or discomfort in my chest or rib area.' },
  { id: 'qy04', text: "I feel a lump in my throat that I can't swallow or cough up." },

  { id: 'tb01', text: "I sneeze or get a stuffy nose even when I'm not sick." },
  { id: 'tb02', text: "I'm prone to allergies (medications, food, scents, pollen, or seasonal changes)." },
  { id: 'tb03', text: 'My skin easily breaks out in hives or welts.' },
  { id: 'tb04', text: 'My skin turns red and marks easily when scratched.' }
];

const ANSWER_SCALE = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];

// `reverse` belongs to a (type, question) pair, not to the question itself: qx01/qx04 score
// forward for Qi Deficiency and reverse for Balanced. Never assume a type's four items sit
// next to each other in QUESTIONS — always read scores through this map.
const TYPE_SCORING_MAP = {
  pinghe: [
    { id: 'px01', reverse: false }, { id: 'qx01', reverse: true },
    { id: 'qx04', reverse: true }, { id: 'px04', reverse: false }
  ],
  qixu: [
    { id: 'qx01', reverse: false }, { id: 'qx02', reverse: false },
    { id: 'qx03', reverse: false }, { id: 'qx04', reverse: false }
  ],
  yangxu: [
    { id: 'yx01', reverse: false }, { id: 'yx02', reverse: false },
    { id: 'yx03', reverse: false }, { id: 'yx04', reverse: false }
  ],
  yinxu: [
    { id: 'yinx01', reverse: false }, { id: 'yinx02', reverse: false },
    { id: 'yinx03', reverse: false }, { id: 'yinx04', reverse: false }
  ],
  tanshi: [
    { id: 'ts01', reverse: false }, { id: 'ts02', reverse: false },
    { id: 'ts03', reverse: false }, { id: 'ts04', reverse: false }
  ],
  shire: [
    { id: 'sr01', reverse: false }, { id: 'sr02', reverse: false },
    { id: 'sr03', reverse: false }, { id: 'sr04', reverse: false }
  ],
  xueyu: [
    { id: 'xy01', reverse: false }, { id: 'xy02', reverse: false },
    { id: 'xy03', reverse: false }, { id: 'xy04', reverse: false }
  ],
  qiyu: [
    { id: 'qy01', reverse: false }, { id: 'qy02', reverse: false },
    { id: 'qy03', reverse: false }, { id: 'qy04', reverse: false }
  ],
  tebing: [
    { id: 'tb01', reverse: false }, { id: 'tb02', reverse: false },
    { id: 'tb03', reverse: false }, { id: 'tb04', reverse: false }
  ]
};

// Fixed presentation order, same for every visitor — no per-session shuffling. No two adjacent
// items belong to the same type, and the four rounds do not repeat one type sequence.
const DISPLAY_ORDER = [
  'qx01', 'tb01', 'yinx01', 'xy01', 'sr01', 'yx01', 'ts01', 'qy01', 'px01',
  'sr02', 'qy02', 'qx02', 'ts02', 'tb02', 'xy02', 'yx02', 'yinx02',
  'yx03', 'yinx03', 'tb03', 'qy03', 'xy03', 'qx03', 'sr03', 'ts03', 'px04',
  'yinx04', 'ts04', 'sr04', 'xy04', 'yx04', 'qx04', 'qy04', 'tb04'
];

// Canonical order of the eight deviation types. Doubles as the tie-break for equal scores, which
// happen often because transformed scores are always multiples of 6.25.
const DEVIATION_TYPES = ['qixu', 'yangxu', 'yinxu', 'tanshi', 'shire', 'xueyu', 'qiyu', 'tebing'];

const TYPE_LABEL = {
  pinghe: 'Balanced Constitution',
  qixu: 'Qi Deficiency',
  yangxu: 'Yang Deficiency',
  yinxu: 'Yin Deficiency',
  tanshi: 'Phlegm-Damp',
  shire: 'Damp-Heat',
  xueyu: 'Blood Stasis',
  qiyu: 'Qi Stagnation',
  tebing: 'Special Constitution'
};

const SCALE_MAX = 5;
const MAIN_THRESHOLD = 40;   // >= 40 → main tendency
const MILD_THRESHOLD = 30;   // [30, 40) → mild tendency
const PINGHE_THRESHOLD = 60; // >= 60 → balanced
const STRONG_THRESHOLD = 60; // >= 60 → "strongly present"
const MAX_MAIN = 3;
const MAX_MILD = 3;

// ─── Products ─────────────────────────────────────────────────
// `url` is global to a product; `enabled` and `copy` live on each per-constitution entry in
// CONTENT, so Phlegm-Damp can switch its moxa note off while Qi Deficiency keeps it on.
// `precaution: true` marks physical products whose product page carries a precautions list —
// that pointer sentence is only appended once the product has a real url.
const PRODUCTS = {
  moxa: { url: null, precaution: true },
  patch: { url: null, precaution: true },
  ed_ebook: { url: null, precaution: false },
  tinnitus_ebook: { url: null, precaution: false },
  jieyu_footsoak: { url: null, precaution: true },   // Calm Qi footsoak — pending launch
  wendan_footsoak: { url: null, precaution: true }   // Clear-Damp footsoak — pending launch
};

const PRECAUTION_NOTE = 'Pregnant or sensitive-skin users should check the product page for precautions first.';

// Only reachable once wendan_footsoak is enabled for both Phlegm-Damp and Damp-Heat. Needs
// Gloria's sign-off on the wording before that switch is flipped.
const WENDAN_COMBINED_NOTE = 'Both variations suit you — the original formula is a reasonable place to start.';

// ─── Constitution content (finalized copy — do not reword) ────
const CONTENT = {
  pinghe: {
    description: "Your answers suggest a fairly balanced constitution — a relatively good state, though it's not fixed, and still worth paying attention to.",
    descriptionMild: "Your answers don't show a strongly dominant pattern in either direction — the closest match is a Balanced Constitution, though on the milder side. The general guidance below for Balanced Constitution is a reasonable starting point.",
    diet: [{ id: 'diet-varied', text: 'A varied, balanced diet is enough — adjust lightly with the seasons (lighter in summer, warmer in winter).' }],
    lifestyle: [{ id: 'lifestyle-routine', text: 'Keep a regular routine and moderate exercise; avoid long stretches of high stress or sleep deprivation that could tip the balance.' }],
    book: 'See the "Balanced Constitution" section in the ebook for the full explanation.',
    ebooks: [],
    products: []
  },

  qixu: {
    description: 'Based on your answers, a Qi Deficiency pattern stands out — often showing up as fatigue, shortness of breath, and a weak voice. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-qixu', text: 'Foods like Chinese yam, jujube, and millet are traditionally used in moderation; avoid excess raw, cold, or greasy food.' }],
    lifestyle: [{ id: 'lifestyle-gentle-movement', text: 'Avoid overexertion and late nights; favor gentle movement like walking or tai chi, and prioritize enough sleep.' }],
    book: 'See the "Qi Deficiency" section in the ebook for the full explanation.',
    ebooks: [],
    products: [
      { product: 'moxa', enabled: true, copy: 'Warming key points with suspension moxibustion is one of the most traditional ways to support Qi — many people build it into a short weekly ritual. Our moxa stick is designed for exactly this kind of home practice.' }
    ]
  },

  yangxu: {
    description: 'Based on your answers, a Yang Deficiency pattern stands out — often showing up as feeling cold, cold hands and feet, and a preference for warm drinks. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-yangxu', text: 'Warming foods like ginger, longan, and lamb are traditionally used in moderation; cut back on watermelon, cold drinks, and other cooling foods.' }],
    lifestyle: [{ id: 'lifestyle-yangxu-warmth', text: 'Keep your abdomen, lower back, and feet warm; avoid prolonged sitting and late nights.' }],
    book: 'See the "Yang Deficiency" section in the ebook for the full explanation.',
    ebooks: [
      { product: 'ed_ebook', enabled: true, copy: 'Yang Deficiency is the pattern most commonly linked to these concerns in TCM. Our ED guide goes deeper into the Kidney-Yang connection, with practical steps you can start applying right away.' }
    ],
    products: [
      { product: 'patch', enabled: true, copy: 'Since nighttime bathroom trips often trace back to weaker Kidney-Yang, applying our patch to Guanyuan and Baliao is a simple nightly ritual many people build in — no pills, no appointments, just a few minutes before bed.' }
    ]
  },

  yinxu: {
    description: 'Based on your answers, a Yin Deficiency pattern stands out — often showing up as warm palms and soles, a dry mouth, and a tendency to overheat. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-yinxu', text: 'Nourishing foods like white fungus, lily bulb, and pear are traditionally used in moderation; cut back on spicy or fried food.' }],
    lifestyle: [{ id: 'lifestyle-yinxu-noheat', text: 'Avoid late nights (the single most Yin-depleting habit), and avoid prolonged exposure to hot, dry environments.' }],
    book: 'See the "Yin Deficiency" section in the ebook for the full explanation.',
    ebooks: [
      { product: 'tinnitus_ebook', enabled: true, copy: 'Tinnitus is one of the patterns most commonly linked to Yin Deficiency in TCM. Our Tinnitus guide goes deeper into the Kidney-Yin connection, with steps you can start applying today.' }
    ],
    // Intentionally empty: warming tools and the meridian patch are contraindicated here.
    // Do not add a substitute product.
    products: []
  },

  tanshi: {
    description: 'Based on your answers, a Phlegm-Damp pattern stands out — often showing up as heaviness in the body, a soft midsection, and excess phlegm. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-tanshi', text: "Foods like Job's tears, adzuki bean, and winter melon are traditionally used in moderation; cut back on sweets, greasy food, and alcohol." }],
    lifestyle: [{ id: 'lifestyle-sweat-exercise', text: 'Regular exercise (to support healthy sweating) and a dry, well-ventilated living space help.' }],
    book: 'See the "Phlegm-Damp" section in the ebook for the full explanation.',
    ebooks: [],
    // These two are mutually exclusive. When the Clear-Damp footsoak launches, flip
    // wendan_footsoak to enabled:true and moxa to enabled:false in the same edit.
    products: [
      { product: 'wendan_footsoak', enabled: false, copy: 'A footsoak formula for damp conditions — coming soon.' },
      { product: 'moxa', enabled: true, copy: 'Since Phlegm-Damp often responds well to warmth, suspension moxibustion is a traditional way to help move dampness along — many people use our moxa stick for exactly this.' }
    ]
  },

  shire: {
    description: 'Based on your answers, a Damp-Heat pattern stands out — often showing up as an oily complexion, breakouts, and a bitter taste in the mouth. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-shire', text: 'Foods like mung bean, winter melon, and bitter melon are traditionally used in moderation; cut back on spicy, fried food, and alcohol.' }],
    lifestyle: [{ id: 'lifestyle-shire-ventilation', text: 'Avoid late nights, keep your space well-ventilated, and avoid prolonged stress or irritability.' }],
    book: 'See the "Damp-Heat" section in the ebook for the full explanation.',
    ebooks: [],
    // Unlike Phlegm-Damp, this type gets NO backup product while the footsoak is pending —
    // warming tools like the moxa stick are contraindicated for Damp-Heat.
    products: [
      { product: 'wendan_footsoak', enabled: false, copy: 'A footsoak formula for damp-heat conditions — coming soon.' }
    ]
  },

  xueyu: {
    description: 'Based on your answers, a Blood Stasis pattern stands out — often showing up as a dull complexion, easy bruising, and forgetfulness. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-xueyu', text: 'Foods like hawthorn and rose tea are traditionally used in moderation; pregnant or menstruating women should check with a professional first.' }],
    lifestyle: [{ id: 'lifestyle-circulation', text: 'Regular movement to support circulation, avoiding prolonged emotional suppression, and staying warm all help.' }],
    book: 'See the "Blood Stasis" section in the ebook for the full explanation.',
    ebooks: [],
    products: [
      { product: 'moxa', enabled: true, copy: 'Blood Stasis is closely tied to sluggish circulation, and warming therapies like moxibustion are among the most traditional ways to support movement in the body. Our moxa stick is built for this kind of home practice.' }
    ]
  },

  qiyu: {
    description: 'Based on your answers, a Qi Stagnation pattern stands out — often showing up as low mood, sighing, and tightness in the chest or ribs. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-qiyu', text: 'Rose tea and dried tangerine peel are traditionally used in moderation; avoid bottling up emotions or stress-eating.' }],
    lifestyle: [{ id: 'lifestyle-emotional-outlet', text: 'Actively find outlets for your emotions (movement, connection, hobbies); if this is an ongoing struggle, professional support is worth considering.' }],
    book: 'See the "Qi Stagnation" section in the ebook for the full explanation.',
    ebooks: [],
    // No substitute product while the Calm Qi footsoak is pending.
    products: [
      { product: 'jieyu_footsoak', enabled: false, copy: 'A footsoak formula to help ease stagnation — coming soon.' }
    ]
  },

  tebing: {
    description: 'Based on your answers, a tendency toward a Special (allergy-prone) Constitution stands out. This is a tendency, not a diagnosis.',
    diet: [{ id: 'diet-tebing', text: 'Be mindful of known allergens — foods, environment, or contact triggers.' }],
    lifestyle: [{ id: 'lifestyle-allergen-control', text: 'Keep your living space clean to reduce common triggers like dust mites; if you have a known allergy history, a doctor or allergy specialist is the best first stop.' }],
    book: 'See the "Special Constitution" section in the ebook for the full explanation.',
    // Intentionally empty: no product or cross-sell suggestions for allergy-related constitutions.
    ebooks: [],
    products: []
  }
};

const COMPOSITE_INTRO = "Your answers reflect more than one constitution pattern — that's common. We'd suggest reading through the matching sections of the ebook together.";
const MILD_SECTION_NOTE = 'You can find the full explanation for each of these in the ebook as well.';

const DISCLAIMER_RESULT = 'Your result reflects a self-assessment based on TCM constitution theory, for educational and self-awareness purposes only. It is not a clinical diagnosis. If you have ongoing health concerns, please consult a licensed physician or qualified TCM practitioner before making changes to your diet, lifestyle, or supplement routine.';

// UI microcopy — written during implementation, safe to reword.
const UI = {
  sectionSummary: 'What Stands Out',
  sectionPatterns: 'What This Means',
  sectionDiet: 'Diet Directions',
  sectionLifestyle: 'Lifestyle',
  sectionLibrary: 'From the Library',
  sectionProducts: 'Worth a Look',
  // The spec words this section as "Also worth noting: <types>". That framing lives in the
  // section heading, so the card itself just lists the types instead of repeating the phrase.
  sectionMild: 'Also Worth Noting',
  screenNotes: [
    'Take your time — go with your first instinct.',
    'Nice — keep going.',
    "You're doing great.",
    'Over halfway there.',
    'Almost done.',
    'Last few.'
  ],
  incompleteHint: 'Please answer all the statements on this screen to continue.'
};

const SCREEN_SIZES = [6, 6, 6, 6, 6, 4]; // 34 items across 6 screens

// ─── Scoring ──────────────────────────────────────────────────
function scoreConstitutions(answers) {
  const scores = {};
  for (const type of Object.keys(TYPE_SCORING_MAP)) {
    const items = TYPE_SCORING_MAP[type];
    let raw = 0;
    for (const item of items) {
      const value = answers[item.id];
      if (typeof value !== 'number' || value < 1 || value > SCALE_MAX) {
        throw new Error('Missing or invalid answer for ' + item.id);
      }
      raw += item.reverse ? (SCALE_MAX + 1) - value : value;
    }
    scores[type] = ((raw - items.length) / (items.length * (SCALE_MAX - 1))) * 100;
  }
  return scores;
}

function strengthLabel(score) {
  return score >= STRONG_THRESHOLD ? 'strongly present' : 'present';
}

// Interval checks are left-closed/right-open by design ([30,40) etc.). In practice transformed
// scores are always multiples of 6.25, so they never land exactly on 30, 40, or 60.
function classify(scores) {
  const byScoreDesc = (a, b) =>
    (scores[b] - scores[a]) || (DEVIATION_TYPES.indexOf(a) - DEVIATION_TYPES.indexOf(b));

  const atMain = DEVIATION_TYPES.filter(t => scores[t] >= MAIN_THRESHOLD).sort(byScoreDesc);
  const atMild = DEVIATION_TYPES
    .filter(t => scores[t] >= MILD_THRESHOLD && scores[t] < MAIN_THRESHOLD)
    .sort(byScoreDesc);

  // Step 2/3: any deviation type at or above 40 wins outright. Balanced is not shown or
  // mentioned from here on, however high pingheScore happens to be.
  if (atMain.length) {
    const main = atMain.slice(0, MAX_MAIN);
    // Overflow types all score >= 40, so they stay ahead of the [30,40) group without re-sorting.
    const mild = atMain.slice(MAX_MAIN).concat(atMild).slice(0, MAX_MILD);
    return { kind: 'deviation', main, mild, composite: main.length > 1 };
  }

  // Step 4: every deviation type is below 40.
  if (scores.pinghe >= PINGHE_THRESHOLD) {
    if (!atMild.length) return { kind: 'balanced', main: ['pinghe'], tendencies: [], mild: [], composite: false };
    // The [30,40) types are named in the headline here, so the "Also worth noting" section is
    // skipped to avoid listing them twice.
    return { kind: 'balanced-with-tendency', main: ['pinghe'], tendencies: atMild.slice(0, MAX_MILD), mild: [], composite: false };
  }
  return { kind: 'balanced-mild', main: ['pinghe'], tendencies: [], mild: atMild.slice(0, MAX_MILD), composite: false };
}

// ─── Report assembly ──────────────────────────────────────────
// Bullets are deduped by exact id match only — no semantic or near-duplicate matching. The first
// occurrence wins, so the highest-scoring type's wording is the one that survives.
function collectBullets(types, key) {
  const seen = new Set();
  const out = [];
  for (const type of types) {
    for (const bullet of CONTENT[type][key]) {
      if (seen.has(bullet.id)) continue;
      seen.add(bullet.id);
      out.push(bullet);
    }
  }
  return out;
}

// Product/ebook notes dedupe on the product key, not on the copy. The moxa stick appears under
// Qi Deficiency, Blood Stasis and Phlegm-Damp with three deliberately different sentences — they
// are NOT duplicates of each other. When a composite result hits two of those types, show only
// the note belonging to the higher-scoring type and drop the other outright: `types` arrives in
// score-descending order, so the first occurrence is the one to keep. Never show both, and never
// treat the differing copy as a reason to keep a second note.
function collectProductNotes(types, key) {
  const seen = new Set();
  const out = [];
  for (const type of types) {
    for (const entry of CONTENT[type][key]) {
      if (!entry.enabled || seen.has(entry.product)) continue;
      seen.add(entry.product);
      const product = PRODUCTS[entry.product];
      out.push({
        product: entry.product,
        // Which constitution this note belongs to. In a composite report the notes are pooled
        // into one section, so without this a visitor who also leans Yin Deficiency would see a
        // moxa note with nothing tying it to the Qi Deficiency pattern it was written for.
        forType: type,
        forLabel: TYPE_LABEL[type],
        copy: entry.copy,
        url: product.url,
        precaution: product.precaution && product.url !== null ? PRECAUTION_NOTE : null
      });
    }
  }
  return out;
}

function wendanCombinedNote(types) {
  const bothTypes = types.indexOf('tanshi') !== -1 && types.indexOf('shire') !== -1;
  if (!bothTypes) return null;
  const enabledSomewhere = types.some(type =>
    CONTENT[type].products.some(p => p.product === 'wendan_footsoak' && p.enabled)
  );
  return enabledSomewhere ? WENDAN_COMBINED_NOTE : null;
}

function buildReport(answers) {
  const scores = scoreConstitutions(answers);
  const verdict = classify(scores);
  const types = verdict.main;

  const headline = types.map(type => ({
    type,
    label: TYPE_LABEL[type],
    strength: verdict.kind === 'deviation' ? strengthLabel(scores[type]) : null
  }));

  let title = null;
  if (verdict.kind === 'balanced-with-tendency') {
    title = 'Balanced, with a tendency toward ' + verdict.tendencies.map(t => TYPE_LABEL[t]).join(', ');
  }

  const patterns = types.map(type => ({
    type,
    label: TYPE_LABEL[type],
    strength: verdict.kind === 'deviation' ? strengthLabel(scores[type]) : null,
    description: verdict.kind === 'balanced-mild' ? CONTENT[type].descriptionMild : CONTENT[type].description,
    book: CONTENT[type].book
  }));

  const products = collectProductNotes(types, 'products');
  const combined = wendanCombinedNote(types);
  if (combined) {
    const wendan = products.find(p => p.product === 'wendan_footsoak');
    if (wendan) wendan.copy = wendan.copy + ' ' + combined;
  }

  // Transformed scores deliberately do not travel with the report: the result page never shows
  // numbers, and leaving them out makes "Balanced is never mentioned once a deviation hits 40"
  // a structural guarantee rather than a rendering convention.
  return {
    kind: verdict.kind,
    title,
    headline,
    compositeIntro: verdict.composite ? COMPOSITE_INTRO : null,
    patterns,
    diet: collectBullets(types, 'diet'),
    lifestyle: collectBullets(types, 'lifestyle'),
    ebooks: collectProductNotes(types, 'ebooks'),
    products,
    mild: verdict.mild.length
      ? { labels: verdict.mild.map(t => TYPE_LABEL[t]), note: MILD_SECTION_NOTE }
      : null
  };
}

// ─── Browser-only code below ──────────────────────────────────
// Guarded so this file can also be required in plain Node for regression testing of the
// scoring/classification logic, exactly like bornkey.js does.
if (typeof document !== 'undefined') {
  const STORAGE_KEY = 'gloriaConstitutionQuiz';
  const FILE_NAME = 'wellnesswithgloria-constitution-result.png';

  const state = { answers: {}, screen: 0, submitted: false };

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function showScreen(id) {
    document.querySelectorAll('.cq-screen').forEach(s => s.classList.remove('active'));
    document.getElementById(id).classList.add('active');
    window.scrollTo(0, 0);
  }

  function saveState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* private mode — losing progress on refresh is acceptable */ }
  }

  function clearState() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  }

  function screenSlice(index) {
    let start = 0;
    for (let i = 0; i < index; i++) start += SCREEN_SIZES[i];
    return DISPLAY_ORDER.slice(start, start + SCREEN_SIZES[index]);
  }

  function questionText(id) {
    const q = QUESTIONS.find(item => item.id === id);
    return q ? q.text : '';
  }

  function renderQuizScreen() {
    const ids = screenSlice(state.screen);
    const html = ids.map(id => {
      const selected = state.answers[id];
      const dots = ANSWER_SCALE.map((label, i) => {
        const value = i + 1;
        const isOn = selected === value ? ' selected' : '';
        return '<button type="button" class="cq-dot cq-dot-' + value + isOn + '"' +
          ' data-value="' + value + '" role="radio" aria-checked="' + (selected === value) + '"' +
          ' aria-label="' + escapeHtml(label) + '"><span class="cq-dot-mark"></span></button>';
      }).join('');
      return '<div class="cq-question" data-id="' + id + '">' +
        '<p class="cq-question-text">' + escapeHtml(questionText(id)) + '</p>' +
        '<div class="cq-scale" role="radiogroup" aria-label="' + escapeHtml(questionText(id)) + '">' + dots + '</div>' +
        '<div class="cq-scale-anchors"><span>' + ANSWER_SCALE[0] + '</span><span>' + ANSWER_SCALE[ANSWER_SCALE.length - 1] + '</span></div>' +
        '<p class="cq-scale-readout" aria-live="polite">' + (selected ? escapeHtml(ANSWER_SCALE[selected - 1]) : '') + '</p>' +
        '</div>';
    }).join('');

    document.getElementById('cq-questions').innerHTML = html;
    document.getElementById('cq-screen-note').textContent = UI.screenNotes[state.screen] || '';
    document.getElementById('cq-progress-bar').style.width =
      (((state.screen + 1) / SCREEN_SIZES.length) * 100).toFixed(1) + '%';
    document.getElementById('cq-btn-back').style.visibility = state.screen === 0 ? 'hidden' : 'visible';
    document.getElementById('cq-btn-continue').textContent =
      state.screen === SCREEN_SIZES.length - 1 ? 'See My Result' : 'Continue';
    hideHint();
  }

  function hideHint() {
    const hint = document.getElementById('cq-screen-hint');
    hint.textContent = '';
    hint.classList.remove('visible');
  }

  function currentScreenComplete() {
    return screenSlice(state.screen).every(id => typeof state.answers[id] === 'number');
  }

  function handleScaleClick(event) {
    const dot = event.target.closest('.cq-dot');
    if (!dot) return;
    const question = dot.closest('.cq-question');
    const id = question.getAttribute('data-id');
    const value = parseInt(dot.getAttribute('data-value'), 10);

    state.answers[id] = value;
    saveState();

    question.querySelectorAll('.cq-dot').forEach(node => {
      const on = node === dot;
      node.classList.toggle('selected', on);
      node.setAttribute('aria-checked', String(on));
    });
    question.querySelector('.cq-scale-readout').textContent = ANSWER_SCALE[value - 1];
    if (currentScreenComplete()) hideHint();
  }

  function goToScreen(index) {
    state.screen = index;
    saveState();
    renderQuizScreen();
  }

  function submitQuiz() {
    state.submitted = true;
    saveState();
    showScreen('cq-screen-loading');
    const bar = document.getElementById('cq-loading-bar');
    bar.style.width = '0%';
    void bar.offsetWidth;
    bar.style.width = '100%';
    setTimeout(() => {
      renderReport(buildReport(state.answers));
      showScreen('cq-screen-result');
    }, 1600);
  }

  function sectionHtml(title, body, extraClass) {
    return '<div class="cq-section ' + (extraClass || '') + '">' +
      '<div class="cq-section-title"><span class="cq-section-mark"></span>' + escapeHtml(title) + '</div>' +
      body + '</div>';
  }

  function bulletListHtml(bullets) {
    return '<div class="cq-card"><ul class="cq-bullets">' +
      bullets.map(b => '<li>' + escapeHtml(b.text) + '</li>').join('') +
      '</ul></div>';
  }

  function productListHtml(notes, showOwner) {
    return notes.map(note => {
      const link = note.url
        ? '<a class="cq-note-link" href="' + escapeHtml(note.url) + '" target="_blank" rel="noopener">Take a look →</a>'
        : '';
      const precaution = note.precaution
        ? '<p class="cq-note-precaution">' + escapeHtml(note.precaution) + '</p>'
        : '';
      const owner = showOwner
        ? '<p class="cq-note-for">' + escapeHtml('For your ' + note.forLabel + ' tendency') + '</p>'
        : '';
      return '<div class="cq-card cq-note">' +
        owner +
        '<p class="cq-note-copy">' + escapeHtml(note.copy) + '</p>' +
        precaution + link +
        '</div>';
    }).join('');
  }

  function renderReport(report) {
    const summaryItems = report.title
      ? '<div class="cq-headline">' + escapeHtml(report.title) + '</div>'
      : report.headline.map(item =>
          '<div class="cq-headline-row">' +
            '<span class="cq-headline-name">' + escapeHtml(item.label) + '</span>' +
            (item.strength ? '<span class="cq-pill">' + escapeHtml(item.strength) + '</span>' : '') +
          '</div>').join('');

    let html = sectionHtml(UI.sectionSummary,
      '<div class="cq-card cq-card-accent">' + summaryItems +
      (report.compositeIntro ? '<p class="cq-card-desc cq-composite-intro">' + escapeHtml(report.compositeIntro) + '</p>' : '') +
      '</div>', 'cq-bg-default');

    html += sectionHtml(UI.sectionPatterns,
      report.patterns.map(p =>
        '<div class="cq-card">' +
          '<p class="cq-card-desc">' + escapeHtml(p.description) + '</p>' +
          '<hr class="cq-card-divider">' +
          '<p class="cq-card-meta">' + escapeHtml(p.book) + '</p>' +
        '</div>').join(''), 'cq-bg2');

    html += sectionHtml(UI.sectionDiet, bulletListHtml(report.diet), 'cq-bg-default');
    html += sectionHtml(UI.sectionLifestyle, bulletListHtml(report.lifestyle), 'cq-bg2');

    // The owner caption only earns its space once more than one pattern is on the page.
    const showOwner = report.patterns.length > 1;
    if (report.ebooks.length) {
      html += sectionHtml(UI.sectionLibrary, productListHtml(report.ebooks, showOwner), 'cq-bg-default');
    }
    if (report.products.length) {
      html += sectionHtml(UI.sectionProducts, productListHtml(report.products, showOwner), 'cq-bg3');
    }
    if (report.mild) {
      html += sectionHtml(UI.sectionMild,
        '<div class="cq-card">' +
          '<p class="cq-card-desc cq-mild-list">' + escapeHtml(report.mild.labels.join(', ')) + '</p>' +
          '<p class="cq-card-meta">' + escapeHtml(report.mild.note) + '</p>' +
        '</div>', 'cq-bg-default');
    }

    document.getElementById('cq-report-body').innerHTML = html;
  }

  // ─── Download (html2canvas) ─────────────────────────────────
  // The capture target (#cq-report) includes the result disclaimer, so the saved image carries
  // it too. The download/retake buttons and the reserved email-capture block sit outside it.
  async function downloadReport() {
    const button = document.getElementById('cq-btn-download');
    const hint = document.getElementById('cq-download-hint');
    if (typeof html2canvas === 'undefined') {
      hint.textContent = "The image tool didn't load — please refresh and try again.";
      return;
    }

    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Preparing your image…';
    try {
      // Without this the capture can fall back to system fonts mid-render.
      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      const canvas = await html2canvas(document.getElementById('cq-report'), {
        backgroundColor: '#f5f0e8',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = FILE_NAME;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      hint.textContent = 'If the image opened instead of saving, press and hold it to save to your photos.';
    } catch (e) {
      hint.textContent = "Couldn't create the image — please try again.";
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }

  function restoreState() {
    let saved;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      saved = JSON.parse(raw);
    } catch (e) { return false; }
    if (!saved || typeof saved.answers !== 'object' || saved.answers === null) return false;

    state.answers = saved.answers;
    state.screen = Math.min(Math.max(saved.screen || 0, 0), SCREEN_SIZES.length - 1);

    if (saved.submitted) {
      try {
        renderReport(buildReport(state.answers));
        state.submitted = true;
        showScreen('cq-screen-result');
        return true;
      } catch (e) {
        clearState();
        return false;
      }
    }
    if (Object.keys(state.answers).length) {
      renderQuizScreen();
      showScreen('cq-screen-quiz');
      return true;
    }
    return false;
  }

  (function init() {
    document.getElementById('cq-btn-start').addEventListener('click', () => {
      renderQuizScreen();
      showScreen('cq-screen-quiz');
    });

    document.getElementById('cq-questions').addEventListener('click', handleScaleClick);

    document.getElementById('cq-btn-back').addEventListener('click', () => {
      if (state.screen > 0) goToScreen(state.screen - 1);
    });

    document.getElementById('cq-btn-continue').addEventListener('click', () => {
      if (!currentScreenComplete()) {
        const hint = document.getElementById('cq-screen-hint');
        hint.textContent = UI.incompleteHint;
        hint.classList.add('visible');
        return;
      }
      if (state.screen === SCREEN_SIZES.length - 1) submitQuiz();
      else goToScreen(state.screen + 1);
    });

    document.getElementById('cq-btn-download').addEventListener('click', downloadReport);

    document.getElementById('cq-btn-retake').addEventListener('click', () => {
      state.answers = {};
      state.screen = 0;
      state.submitted = false;
      clearState();
      document.getElementById('cq-download-hint').textContent = '';
      showScreen('cq-screen-intro');
    });

    restoreState();
  })();
}

// ─── Node test hook ───────────────────────────────────────────
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    QUESTIONS, ANSWER_SCALE, TYPE_SCORING_MAP, DISPLAY_ORDER, DEVIATION_TYPES,
    TYPE_LABEL, CONTENT, PRODUCTS, SCREEN_SIZES,
    MAIN_THRESHOLD, MILD_THRESHOLD, PINGHE_THRESHOLD, STRONG_THRESHOLD, MAX_MAIN, MAX_MILD,
    scoreConstitutions, classify, strengthLabel, buildReport,
    collectBullets, collectProductNotes
  };
}
